// livestreamUtils.js
'use strict';
import {
  LivestreamProvider,
  PRESET_SD,
} from '../resource/liveStreamConstants.js';

import { breakpoint } from '../style/variables.js';

/**
 * Get can unlock livestream show by ticket.
 * @param {string} {productId} - product id.
 * @param {string} {productPrice} - product price.
 * @returns {Boolean}
 */
export const getCanUnlockByTicket = ({ productId, productPrice } = {}) => {
  const hasProductId = productId !== null && productId !== undefined;
  const hasProductPrice = productPrice !== null && productPrice !== undefined;
  if (hasProductId && hasProductPrice) return true;
  return false;
};

/**
 * Get is waiting product data.
 * @param {string} {productId} - product id.
 * @param {string} {productPrice} - product price.
 * @returns {Boolean}
 */
export const getIsWaitingProductData = ({ productId, productPrice } = {}) => {
  const hasProductId = productId !== null && productId !== undefined;
  const hasProductPrice = productPrice !== null && productPrice !== undefined;
  if (hasProductId && !hasProductPrice) return true;
  if (productId === undefined) return true;
  return false;
};

/**
 * Determines if the stream provider is BytePlus.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the provider is BytePlus, false otherwise.
 */
export const getIsByteplusStreamProvider = ({ provider } = {}) =>
  LivestreamProvider.BYTEPLUS === provider;

/**
 * Determines if the stream provider is Agora.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the provider is Agora, false otherwise.
 */
export const getIsAgoraStreamProvider = ({ provider } = {}) =>
  LivestreamProvider.AGORA === provider;

/**
 * Determines if the stream provider support DRM.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the stream provider support DRM, false otherwise.
 */
export const getIsStreamProviderSupportDrm = ({ provider } = {}) =>
  // Only SWAG support DRM now.
  LivestreamProvider.SWAG === provider;

/**
 * Format goal breakdown.
 * @param {object} goalBreakdown - The goal breakdown.
 * @returns {object} The formatted goal breakdown.
 */
export const formatGoalBreakdown = (goalBreakdown = {}) =>
  Object.keys(goalBreakdown).reduce((breakdown, key) => {
    breakdown[key] = {
      id: key,
      progress: goalBreakdown[key],
    };
    return breakdown;
  }, {});

/**
 * Get should show balance warning.
 * @param {object} options - The options object.
 * @param {string} options.preset - The stream preset.
 * @param {string} options.fundingId - The funding id.
 * @param {number} options.streamTokenExp - The stream token expiration unix timestamp.
 * @param {number} options.priceDuration - The price duration.
 * @param {number} options.balance - The balance.
 * @param {number} options.price - The price.
 * @param {number} options.remainingBalanceMultiplier - The remaining balance multiplier.
 * @param {number} options.nowUnix - The current unix timestamp.
 * @returns {boolean} True if the balance warning should be shown, false otherwise.
 */
export const getShouldShowBalanceWarning = ({
  preset = null,
  fundingId = null,
  streamTokenExp = 0,
  priceDuration = 0,
  balance = 0,
  price = 0,
  remainingBalanceMultiplier = 0,
  nowUnix = 0,
} = {}) => {
  const tokenAvailableMin = streamTokenExp
    ? Math.floor((streamTokenExp - nowUnix) / 60)
    : 0;
  return (
    preset === PRESET_SD &&
    !fundingId &&
    streamTokenExp &&
    priceDuration === 1 &&
    balance < price * remainingBalanceMultiplier &&
    tokenAvailableMin < remainingBalanceMultiplier
  );
};

/**
 * Determines if the stream provider is Tencent.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the provider is Tencent, false otherwise.
 */
export const getIsTencentStreamProvider = ({ provider } = {}) =>
  LivestreamProvider.TENCENT === provider;

/**
 * Grid config matching TopHomeLiveStreamFeed's LiveStreamCardsWrapper CSS:
 * - grid-template-columns: repeat(auto-fill, minmax(MIN_CARD_WIDTH, 1fr))
 * - gap: GAP
 * - padding: 0 16px (total 32px)
 */
const TOP_LIVESTREAM_FEED_GRID_CONFIG = {
  padding: 32,
  desktop: { minCardWidth: 320, gap: 12, rows: 1 },
  laptop: { minCardWidth: 280, gap: 12, rows: 1 },
  tablet: { minCardWidth: 200, gap: 8, rows: 2 },
  mobile: { minCardWidth: 160, gap: 8, rows: 2 },
};

/**
 * Get top feed count based on screen width
 * Calculates how many cards fit in the top feed section based on CSS Grid layout
 * @param {object} params
 * @param {number} params.width - screen width
 * @return {number} top feed count (cards per row * rows)
 */
export const getTopLivestreamCountByWidth = ({ width } = {}) => {
  const innerWidth =
    width ??
    (typeof window !== 'undefined' ? window.innerWidth : breakpoint.mobile);

  const config =
    innerWidth > breakpoint.laptop
      ? TOP_LIVESTREAM_FEED_GRID_CONFIG.desktop
      : innerWidth > breakpoint.tablet
        ? TOP_LIVESTREAM_FEED_GRID_CONFIG.laptop
        : innerWidth > breakpoint.mobile
          ? TOP_LIVESTREAM_FEED_GRID_CONFIG.tablet
          : TOP_LIVESTREAM_FEED_GRID_CONFIG.mobile;

  const containerWidth = innerWidth - TOP_LIVESTREAM_FEED_GRID_CONFIG.padding;
  const cardsPerRow =
    Math.floor(
      (containerWidth + config.gap) / (config.minCardWidth + config.gap)
    ) || 1;

  return cardsPerRow * config.rows;
};

/**
 * Determines if the session is rated.
 * @param {object} options - The options object.
 * @param {number} options.ratedAs - The rated as.
 * @returns {boolean} True if the session is rated, false otherwise.
 */
export const getIsRated = ({ ratedAs = undefined } = {}) => {
  // `ratedAs` might be `null` or `0` to `100`.
  // `undefined` means not rated yet
  const isRated = ratedAs !== undefined;
  return isRated;
};
